import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Mail, ShieldCheck, ShieldAlert, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/** Email Branding — owner/admin. Live-preview via sandboxed iframe (srcDoc). */
export default function EmailBrandingSettings() {
  const [state, setState] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/business/email-branding/"),
        api.get("/business/email-branding/preview"),
      ]);
      setState(s.data);
      setPreview(p.data);
    } catch (e) {
      toast.error("Load failed: " + (e?.response?.data?.detail || e.message));
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        outbound_email_from_name: state.from_name === state.business_name ? "" : state.from_name,
        outbound_email_reply_to: state.reply_to_email || "",
        outbound_email_signature: state.signature_html || "",
        outbound_email_logo_url: state.logo_url || "",
        outbound_email_accent_color: state.accent_color || "#FF5A00",
        outbound_email_footer_enabled: !!state.footer_powered_by,
        email_branding_enabled: state.branding_enabled !== false,
      };
      await api.patch("/business/email-branding/", patch);
      toast.success("Branding saved");
      await load();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!window.confirm("Send a TEST email to your own inbox using your tenant's branding?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/business/email-branding/send-test");
      if (data.ok) toast.success(`Sent to ${data.sent_to_masked} (${data.rate_limit.used}/${data.rate_limit.max})`);
      else toast.error("Provider did not confirm delivery");
    } catch (e) {
      const s = e?.response?.status;
      if (s === 429) toast.error(e.response.data.detail);
      else toast.error("Send failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (!state || !preview) return (
    <div className="p-8 text-sm text-zinc-500" data-testid="branding-loading">Loading…</div>
  );

  return (
    <div className="p-6 max-w-6xl" data-testid="email-branding-page">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-[#FF5A00] mb-1">Business Settings</div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
          <Mail className="w-6 h-6" /> Email Branding
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          How your customers see emails from you. Applies to Coach follow-ups, receipts,
          and future customer notifications. Never applied to platform emails (password
          reset, invitations, security).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SETTINGS */}
        <Card className="p-5 space-y-4" data-testid="branding-form">
          <div>
            <Label className="text-xs">Customer-visible sender name</Label>
            <Input value={state.from_name || ""}
                    onChange={(e) => set("from_name", e.target.value)}
                    placeholder={state.business_name}
                    data-testid="from-name" />
            <p className="text-xs text-zinc-500 mt-1">Defaults to your business name.</p>
          </div>
          <div>
            <Label className="text-xs">Reply-To email</Label>
            <Input value={state.reply_to_email || ""}
                    onChange={(e) => set("reply_to_email", e.target.value)}
                    placeholder="support@yourbusiness.com"
                    data-testid="reply-to" />
          </div>
          <div>
            <Label className="text-xs">Logo URL (https only)</Label>
            <Input value={state.logo_url || ""}
                    onChange={(e) => set("logo_url", e.target.value)}
                    placeholder="https://…/logo.png"
                    data-testid="logo-url" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Accent color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={state.accent_color || "#FF5A00"}
                        onChange={(e) => set("accent_color", e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                        data-testid="accent-color" />
                <Input value={state.accent_color || "#FF5A00"}
                        onChange={(e) => set("accent_color", e.target.value)}
                        className="font-mono" />
              </div>
            </div>
            <div className="pt-6">
              <div className="flex items-center gap-2 text-sm">
                <Switch checked={!!state.footer_powered_by}
                         onCheckedChange={(v) => set("footer_powered_by", v)}
                         data-testid="footer-toggle" />
                <span>Powered-by-LeadForge footer</span>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Email signature (HTML allowed but sanitized)</Label>
            <Textarea rows={3} value={state.signature_html || ""}
                       onChange={(e) => set("signature_html", e.target.value)}
                       placeholder="John Smith • Acme Roofing • (555) 123-4567"
                       data-testid="signature" />
          </div>

          {/* Read-only domain block */}
          <div className="border rounded-md p-3 bg-zinc-900/40" data-testid="domain-block">
            <div className="text-xs font-semibold flex items-center gap-1 mb-1">
              {state.using_custom_domain
                ? <><ShieldCheck className="w-3 h-3 text-green-500" /> Verified custom sending domain</>
                : <><ShieldAlert className="w-3 h-3 text-amber-500" /> Platform sender (default)</>}
              <Badge variant="outline" className="ml-auto text-xs">read-only</Badge>
            </div>
            <div className="text-xs text-zinc-500 space-y-0.5 font-mono">
              <div>From: {preview.technical_from_address_masked}</div>
              <div>Status: {preview.custom_domain_status}</div>
            </div>
            {!state.using_custom_domain && (
              <p className="text-xs text-amber-400 mt-2">
                ⚠ Not yet using your own domain. Emails come from the platform sender
                with your business name as the visible sender. Owner cannot self-verify —
                contact LeadForge support to set up SPF/DKIM.
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="save-branding">
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={load} data-testid="refresh-branding">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
            <Button variant="secondary" onClick={sendTest} disabled={busy}
                     data-testid="send-test-btn" className="ml-auto">
              <Send className="w-3 h-3 mr-1" /> {busy ? "Sending…" : "Send test to my inbox"}
            </Button>
          </div>
        </Card>

        {/* PREVIEW — sandboxed iframe */}
        <div className="space-y-4">
          <Card className="p-4" data-testid="preview-current">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm">Preview — current tenant branding</div>
              <Badge>After</Badge>
            </div>
            <div className="text-xs text-zinc-500 mb-2 space-y-0.5">
              <div>From: <span className="font-semibold text-zinc-300">{preview.visible_from_name}</span> &lt;{preview.technical_from_address_masked}&gt;</div>
              <div>Reply-To: {preview.reply_to_address_masked || <span className="text-zinc-600">not set</span>}</div>
            </div>
            <iframe
              srcDoc={preview.sample_html}
              sandbox=""
              className="w-full h-80 rounded border border-zinc-800 bg-white"
              title="After preview"
              data-testid="preview-iframe-after"
            />
          </Card>
          <Card className="p-4 opacity-70" data-testid="preview-before">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm">Before — LeadForge default</div>
              <Badge variant="outline">Before (illustrative only)</Badge>
            </div>
            <iframe
              srcDoc={preview.before_sample_html}
              sandbox=""
              className="w-full h-40 rounded border border-zinc-800 bg-white"
              title="Before preview"
              data-testid="preview-iframe-before"
            />
          </Card>
        </div>
      </div>
    </div>
  );
}
